import React, { useEffect } from "react";
import { AssetPreview } from "./asset-preview";
import { ActionButtons } from "../atoms/action-buttons";
import { Dropdown } from "../atoms/dropdown";
import { useSubmission } from "../../providers/submission-provider";
import { useTasks } from "../../providers/task-provider";
import { Input } from "../atoms/Input";
import { isTextLikelyEnglish, snakeCaseToWords } from "../../utils/common";
import { InputLocation } from "../atoms/input-location";

interface ReviewSubmissionProps {
  pictureFile: File;
  audioFile: File;
  soundType: string;
  onAddMetadata: () => void;
  onBack: () => void;
}

export const AddMetadata: React.FC<ReviewSubmissionProps> = ({
  pictureFile,
  audioFile,
  soundType,
  onAddMetadata,
  onBack,
}) => {
  const { setSubmissionMetadata, setSubmissionProperties, submission } =
    useSubmission();
  const { currentTask } = useTasks();
  const [isFormChecked, setIsFormChecked] = React.useState(false);

  return (
    <>
      <h2 className="text-left mb-2">
        Add Metadata: {soundType}
        {(currentTask?.subcategories?.length || 0) > 1
          ? `- ${submission?.submission_metadata["sub_category"]}`
          : ""}
      </h2>
      <div className="rounded-xl border-dark-gray-1 border text-left p-4 mb-4">
        <h3 className="mb-2">Your Recording</h3>
        <AssetPreview pictureFile={pictureFile} audioFile={audioFile} />
      </div>
      <form className="mb-4">
        {Object.keys(currentTask?.metadata_schema?.properties || {}).map(
          (propertyKey) => {
            const metadataProperty =
              currentTask?.metadata_schema?.properties[propertyKey];
            if (currentTask == null) return null;
            const property =
              currentTask.metadata_schema.properties[propertyKey];
            if (property.type === "string" && property.enum) {
              return (
                <Dropdown
                  label={`${metadataProperty?.label || snakeCaseToWords(propertyKey)}*`}
                  options={property.enum.map((e) => ({ value: e, label: e }))}
                  onChange={(value) =>
                    setSubmissionMetadata({ [propertyKey]: value })
                  }
                  className="mb-4"
                  value={submission?.submission_metadata?.[propertyKey]}
                  error={
                    isFormChecked &&
                    currentTask?.metadata_schema.required.includes(
                      propertyKey,
                    ) &&
                    !submission?.submission_metadata?.[propertyKey]
                      ? "Required Field"
                      : undefined
                  }
                />
              );
            } else if (property.type === "string") {
              return (
                <Input
                  label={`${metadataProperty?.label || snakeCaseToWords(propertyKey)}*`}
                  type="text"
                  onChange={(value) =>
                    setSubmissionMetadata({ [propertyKey]: value })
                  }
                  className="mb-4"
                  value={submission?.submission_metadata?.[propertyKey]}
                  error={
                    isFormChecked &&
                    currentTask?.metadata_schema.required.includes(
                      propertyKey,
                    ) &&
                    (!submission?.submission_metadata?.[propertyKey]
                      ? "Required Field"
                      : !isTextLikelyEnglish(
                            submission?.submission_metadata?.[propertyKey],
                          )
                        ? "Entry should be in english"
                        : false)
                  }
                />
              );
            } else if (property.type === "number") {
              return (
                <Input
                  label={`${metadataProperty?.label || snakeCaseToWords(propertyKey)}*`}
                  type="number"
                  onChange={(value) =>
                    setSubmissionMetadata({ [propertyKey]: Number(value) })
                  }
                  className="mb-4"
                  value={submission?.submission_metadata?.[propertyKey]}
                  error={
                    isFormChecked &&
                    currentTask?.metadata_schema.required.includes(
                      propertyKey,
                    ) &&
                    !submission?.submission_metadata?.[propertyKey]
                      ? "Required Field"
                      : undefined
                  }
                />
              );
            } else if (property.type === "location") {
              return (
                <InputLocation
                  label="Capture location"
                  onChange={(value) =>
                    setSubmissionMetadata({ location: value })
                  }
                  value={submission?.submission_metadata?.["location"]}
                  error={
                    isFormChecked &&
                    currentTask?.metadata_schema.required.includes(
                      "location",
                    ) &&
                    !submission?.submission_metadata?.["location"]
                      ? "Required Field"
                      : undefined
                  }
                />
              );
            }
          },
        )}
      </form>
      <ActionButtons
        primaryText="Review"
        secondaryText="Back"
        onPrimaryClick={() => {
          setIsFormChecked(true);
          if (
            submission?.subcategory_id &&
            currentTask?.metadata_schema.required.reduce((prev, current) => {
              let isLanguageCorrect = true;
              if (
                currentTask?.metadata_schema.properties[current].type ===
                "string"
              ) {
                isLanguageCorrect = isTextLikelyEnglish(
                  submission?.submission_metadata?.[current],
                );
              }

              return (
                prev &&
                submission?.submission_metadata?.[current] !== undefined &&
                isLanguageCorrect
              );
            }, true)
          ) {
            onAddMetadata();
          }
        }}
        onSecondaryClick={onBack}
      />
    </>
  );
};
